import { type ReactNode } from 'react';
import { useMedia } from 'react-use';
import { AuthGuard } from '@hungryroot/customer';
import { useExperiment } from '@hungryroot/experiments';
import { CalloutBoxSelector } from '@hungryroot/funnel';
import { CaretDownNew } from '@hungryroot/icons';
import { Heading } from '@hungryroot/ramen';
import { type Customer } from '@hungryroot/rest';
import { BreakpointQueryLarge } from '@hungryroot/tokens';
import { LOGO_PATH } from '../navigation/desktop/DesktopNav';
import { PrecheckoutPlan } from '../PrecheckoutPlan/PrecheckoutPlan';
import styles from './PlanContainer.module.css';

const NAME_CHARACTER_LENGTH = 30;

interface Props {
  onBackButtonClick: () => void;
  header: ReactNode;
  customer: Partial<
    Pick<Customer, 'first_name' | 'id' | 'cart' | 'did_checkout'>
  >;
}

export const planContentHeader = (
  <nav className={styles.top}>
    <img className={styles.logo} alt="Hungryroot logo" src={LOGO_PATH} />
  </nav>
);

const NavigationBar = ({ header, customer, onBackButtonClick }: Props) => {
  const isDesktop = useMedia(BreakpointQueryLarge);
  const dietDrivenPreferencesVariant = useExperiment(175);
  const budgetRangePlanVariant = useExperiment(177);

  const isInBudgetRangePlanVariant =
    budgetRangePlanVariant !== undefined && budgetRangePlanVariant > 0;

  const payAsYouGoExperiment = useExperiment(173);

  const isInPayAsYouGoExperiment =
    payAsYouGoExperiment !== undefined && payAsYouGoExperiment > 0;

  return (
    <>
      {header}
      <nav className={styles.nav}>
        <button
          data-testid="quiz-back-button"
          onClick={onBackButtonClick}
          aria-label="plan back button"
          className={styles.back}
        >
          <CaretDownNew />
        </button>
        {!isDesktop && (
          <div className={styles['logo-container']}>
            <img
              className={styles.logo}
              alt="Hungryroot logo"
              src={LOGO_PATH}
            />
          </div>
        )}
      </nav>
      {dietDrivenPreferencesVariant !== undefined &&
        dietDrivenPreferencesVariant < 1 &&
        !isInBudgetRangePlanVariant && (
          <Heading variant="H1" className={`${styles.title}`}>
            {!customer?.first_name
              ? 'Recommended for you'
              : `Recommended for ${
                  customer.first_name.length > NAME_CHARACTER_LENGTH
                    ? `${customer.first_name.substring(
                        0,
                        NAME_CHARACTER_LENGTH
                      )}...`
                    : customer.first_name
                }`}
          </Heading>
        )}
      {!isDesktop && !isInPayAsYouGoExperiment && <CalloutBoxSelector />}
    </>
  );
};

type PlanContainerProps = {
  testid?: string;
  pageHeader: ReactNode;
  onBackButtonClick: () => void;
  onRedirect?: () => void;
};

export function PlanContainer(props: PlanContainerProps) {
  const { testid, pageHeader, onBackButtonClick } = props;

  return (
    <AuthGuard
      authenticated={(auth) => {
        return (
          <Plan
            onBackButtonClick={onBackButtonClick}
            pageHeader={pageHeader}
            auth={auth}
            testid={testid}
          ></Plan>
        );
      }}
    />
  );
}

type PlanProps = PlanContainerProps & {
  auth: {
    customerData: Partial<Customer>;
  };
};

const Plan = (props: PlanProps) => {
  const { auth, pageHeader, onBackButtonClick } = props;

  const navBar = (
    <NavigationBar
      onBackButtonClick={onBackButtonClick}
      header={pageHeader}
      customer={auth.customerData}
    />
  );

  return <PrecheckoutPlan navBar={navBar} customer={auth.customerData} />;
};
